'use client'

import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Spinner, useBreakpointValue, useDisclosure} from '@chakra-ui/react'
import CountrySelectorDrawer from '../country-selector-drawer'
import CountrySelectorDropdown from '../country-selector-dropdown'
import CountrySelectorConfirmationDrawer from '../country-selector-confirmation-drawer'
import {useCountrySelector, sortCountries} from '../../hooks/use-country-selector'
import {useLocation} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useHeaderContext} from '../../contexts/header-context'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import { noop } from '@salesforce/retail-react-app/app/utils/utils'

const CountrySelector = ({
    triggerRef = {},
    onShowCountrySelectorOnFooter = noop,
    variant = 'header',
}) => {
    const location = useLocation()
    const {category, product} = useHeaderContext()
    const {locale: originalLocale} = useMultiSite()
    const {data: basket} = useCurrentBasket()
    const deleteBasket = useShopperBasketsMutation('deleteBasket')
    const isMobile = useBreakpointValue({base: true, md: false})
    const {isOpen, onOpen, onClose} = useDisclosure()
    const internalRef = React.useRef()
    // Use triggerRef from props if provided, otherwise use internal ref
    const drawerTriggerRef = triggerRef || internalRef

    const currentLanguage = originalLocale?.id?.replace('-', '_') || 'en_GB'
    const extractedCountryCode = currentLanguage?.split('_')[1] || 'GB'
    const currentCountryCode = extractedCountryCode === 'GB' ? 'UK' : extractedCountryCode
    const pathname = location.pathname.startsWith('/')
        ? location.pathname.substring(1)
        : location.pathname

    const [countries, setCountries] = useState(null)
    const [language, setLanguage] = useState(currentLanguage)
    const [countryCode, setCountryCode] = useState(currentCountryCode)

    // The pathname may be prefixed with a country/locale alias segment (e.g. "/be/g/...",
    // "/de/p/...") added by the country selector itself, so the product/category segment
    // isn't necessarily at the start of the path.
    const isProductOrCategoryPage = /^\/(?:[^/]+\/)?(g|p)\//.test(location.pathname)

    const {countrySelectorData, isLoading: isCountriesDataLoading} = useCountrySelector()

    const transformCountryConfig = (countries) => {
        return countries?.reduce((acc, country) => {
            acc[country.locale] = {
                label: country.label,
                locale: country.locale,
                url: country.url,
                isProductOrCategoryPage: true
            }

            return acc
        }, {})
    }

    useEffect(() => {
        if (isProductOrCategoryPage) {
            const transformedCountries =
                transformCountryConfig(category?.c_countrySelectorURLs) ||
                transformCountryConfig(product?.c_countrySelectorURLs)
            setCountries(sortCountries(transformedCountries))
        } else {
            setCountries(countrySelectorData?.countrySelectorConfig)
        }

        onShowCountrySelectorOnFooter(countrySelectorData?.showCountrySelectorOnFooter)
    }, [countrySelectorData, location?.pathname, category, product])

    const handleLanguageChange = (selectedLanguage, forceRedirect = false) => {
        const selectedConfig = countrySelectorData?.countrySelectorConfig[selectedLanguage]
        const exceptionConfig = countries[selectedLanguage]
        let fullUrl = null

        if (exceptionConfig?.isProductOrCategoryPage) {
            fullUrl = exceptionConfig?.url
        } else {
            const domain = selectedConfig?.domain || window.location.origin
            const path =
                countrySelectorData?.countrySelectorPages[pathname]?.[selectedLanguage] ??
                selectedConfig?.path ??
                pathname
            const fullDomain = domain.startsWith('http') ? domain : `https://${domain}`
            const cleanDomain = fullDomain.replace(/\/$/, '')
            const cleanPath = path.startsWith('/') ? path : `/${path}`
            fullUrl = `${cleanDomain}${cleanPath}`
        }

        const newCountryCode = selectedLanguage?.split('_')[1]
        setCountryCode(newCountryCode === 'GB' ? 'UK' : newCountryCode)
        setLanguage(selectedLanguage)

        if (basket?.productItems?.length > 0 && !forceRedirect) {
            onOpen()
        } else {
            // Close the CountrySelectorDrawer for mobile before redirecting
            if (isMobile && drawerTriggerRef.current?.onClose) {
                drawerTriggerRef.current.onClose()
            }
            window.location.href = fullUrl
        }
    }

    const handleOnConfirmation = (confirmation) => {
        switch (confirmation) {
            case 'yes':
                if (basket && basket?.basketId) {
                    deleteBasket.mutate(
                        {
                            parameters: {basketId: basket.basketId}
                        },
                        {
                            onSuccess: () => {
                                handleLanguageChange(language, true)
                            }
                        }
                    )
                }
                break
            case 'no':
                setCountryCode(currentCountryCode)
                setLanguage(currentLanguage)
                break
            default:
                break
        }
    }

    const handleConfirmationDrawerClose = () => {
        onClose()
        // Close the CountrySelectorDrawer if it's open
        if (isMobile && drawerTriggerRef.current?.onClose) {
            drawerTriggerRef.current.onClose()
        }
    }

    if (isCountriesDataLoading && !countries) {
        return (
            <Box textAlign="center">
                <Spinner size="xs" color="white" />
            </Box>
        )
    }

    return (
        <>
            <Box zIndex={1350}>
                {isMobile ? (
                    <CountrySelectorDrawer
                        variant={variant}
                        countries={countries}
                        language={language}
                        countryCode={countryCode}
                        currency={originalLocale?.preferredCurrency}
                        handleLanguageChange={handleLanguageChange}
                        triggerRef={drawerTriggerRef}
                    />
                ) : (
                    <CountrySelectorDropdown
                        variant={variant}
                        countries={countries}
                        language={language}
                        countryCode={countryCode}
                        currency={originalLocale?.preferredCurrency}
                        handleLanguageChange={handleLanguageChange}
                    />
                )}
            </Box>
            <CountrySelectorConfirmationDrawer
                isOpen={isOpen}
                onClose={handleConfirmationDrawerClose}
                fromCountry={currentCountryCode}
                toCountry={countryCode}
                onConfirmation={handleOnConfirmation}
            />
        </>
    )
}

CountrySelector.propTypes = {
    triggerRef: PropTypes.object
}

export default CountrySelector
